import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  Typography,
} from "@material-ui/core";
import { AppBarButton } from "@ofqual/portal-components/components/SecondaryAppBar";
import { Controller, useForm } from "react-hook-form";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { useDispatch } from "react-redux";
import { ApplicationScopeStatus, Scope } from "../../../../domain/Scope";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { Error } from "../../../common/Error";
import { closeApplication } from "../../../../redux/actions";
import { Warning } from "@material-ui/icons";

type CloseButtonAndDialogProps = {
  orgId: string;
  id: string;
  scopes: Scope[];
  disabled: boolean;
};

export const CloseButtonAndDialog: React.FC<CloseButtonAndDialogProps> = ({
  scopes,
  orgId,
  id,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <AppBarButton
        text="Close"
        tooltip="Close application"
        onClick={() => setIsOpen(true)}
        disabled={disabled}
      />
      {isOpen && (
        <CloseDialog
          scopes={scopes}
          orgId={orgId}
          id={id}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

type CloseDialogProps = {
  scopes: Scope[];
  orgId: string;
  id: string;
  onClose: () => void;
};

const CloseDialog: React.FC<CloseDialogProps> = ({
  orgId,
  id,
  scopes,
  onClose,
}) => {
  const dispatch = useDispatch();

  const { control, errors, handleSubmit } = useForm({
    defaultValues: { note: "", files: null },
  });
  const onSubmit = (data: { files: File[]; note: string }) => {
    dispatch(closeApplication(orgId, id, data.note, data.files[0]));
    onClose();
  };

  const canClose =
    scopes &&
    scopes.every((scope) => scope.status !== ApplicationScopeStatus.NotSet);

  return (
    <>
      <Dialog
        open={true}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        data-test="close-dialog"
      >
        <DialogTitle data-test="close-dialog-title">
          Close application
        </DialogTitle>

        <DialogContent>
          <Typography variant="h6">Decisions</Typography>
          <List>
            {scopes.map((s) => (
              <ListItem key={s.id}>
                <Grid container spacing={1}>
                  <Grid item xs={9}>
                    <Typography variant="body1">{s.title}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body1">
                      {s.status === 0
                        ? "Not Set"
                        : ApplicationScopeStatus[s.status]}
                    </Typography>
                  </Grid>
                </Grid>
              </ListItem>
            ))}
          </List>
          {!canClose ? (
            <>
              <Grid
                container
                direction="row"
                alignItems="center"
                style={{ marginBottom: 10 }}
              >
                <Grid item>
                  <Warning />
                </Grid>
                <Grid item>
                  <Typography variant="body1">
                    You must make a decision on all scopes before closing the
                    application
                  </Typography>
                </Grid>
              </Grid>
            </>
          ) : null}
          <form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="files"
                  rules={{ required: true }}
                  as={<FileInput />}
                />
                {errors.files && (
                  <Error>&nbsp;&nbsp;Please select an attachment</Error>
                )}
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="note"
                  control={control}
                  as={
                    <TextField
                      label="Message (Optional)"
                      multiline
                      fullWidth
                      rows={5}
                      data-test="message"
                    />
                  }
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            color="primary"
            disabled={!canClose}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
